import React, { useEffect, useState } from "react";
import {
	Card,
	Row,
	Col,
	Typography,
	Divider,
	Tag,
	Space,
	Table,
	Badge,
	Descriptions,
	Alert,
	Button,
	message,
} from "antd";
import {
	InfoCircleOutlined,
	CloudServerOutlined,
	CodeOutlined,
	BugOutlined,
	GithubOutlined,
	GlobalOutlined,
	CalendarOutlined,
	TeamOutlined,
	HistoryOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { PiHouseDuotone } from "react-icons/pi";
import { useNavigate } from "react-router";

const { Title, Text, Paragraph } = Typography;

// Import data package.json (Anda mungkin perlu menyesuaikan path ini)
// Untuk saat ini, kita akan mendefinisikan data secara langsung karena kita tidak bisa mengimpor JSON saat runtime
import packageInfo from "../../../../package.json";

// Data changelog berdasarkan commit git sebenarnya dari repositori ini
// Struktur data ini memudahkan untuk memelihara dan memperbarui dengan rilis baru
const changelogData = [
	{
		version: "0.0.13",
		date: "2025-09-24",
		type: "minor",
		summary: "Penambahan kontrol aplikasi APLA dan perbaikan detail instance",
		changes: [
			{
				type: "added",
				description: "Fitur redeploy, restart, dan stop untuk aplikasi APLA dengan kontrol penuh manajemen instance",
			},
			{
				type: "improved",
				description: "Pembaruan detail akun WEHO dengan antarmuka yang lebih baik dan informasi yang lebih lengkap",
			},
			{
				type: "fixed",
				description: "Perbaikan masalah pada halaman detail instance untuk stabilitas yang lebih baik",
			},
		],
	},
	{
		version: "0.0.12",
		date: "2025-09-23",
		type: "minor",
		summary: "Pembaruan manajemen aplikasi dan perbaikan detail instance",
		changes: [
			{
				type: "added",
				description: "Fitur pembaruan domain aplikasi untuk manajemen domain yang lebih baik",
			},
			{
				type: "added",
				description: "Fitur pembaruan environment variables aplikasi",
			},
			{
				type: "fixed",
				description: "Perbaikan masalah pada detail aplikasi APLA instance",
			},
			{
				type: "added",
				description: "Layanan APLA baru (apla.service.js) untuk komunikasi API yang lebih baik",
			},
		],
	},
	{
		version: "0.0.11",
		date: "2025-09-21",
		type: "minor",
		summary: "Peningkatan pengalaman pengguna untuk pemesanan domain opsional",
		changes: [
			{
				type: "improved",
				description:
					"Antarmuka pemesanan domain yang lebih intuitif untuk layanan non-WEHO dengan pilihan domain opsional",
			},
			{
				type: "added",
				description:
					"Kartu pilihan domain opsional yang memberikan kontrol penuh kepada pengguna untuk menambah atau tidak menambah domain",
			},
			{
				type: "improved",
				description:
					"Validasi dan alur pemesanan yang lebih cerdas untuk membedakan domain wajib (WEHO) vs opsional (layanan lain)",
			},
			{
				type: "improved",
				description:
					"Pesan bantuan dan indikator status yang lebih jelas untuk domain wajib vs opsional",
			},
		],
	},
	{
		version: "0.0.10",
		date: "2025-09-20",
		type: "minor",
		summary: "Manajemen alamat pengguna dan pembaruan halaman pengaturan",
		changes: [
			{
				type: "added",
				description:
					"Fitur manajemen alamat pengguna (lihat, tambah, ubah, hapus) pada halaman Pengaturan > Alamat",
			},
			{
				type: "improved",
				description: "Pembaruan dan perapihan tampilan halaman pengaturan pengguna",
			},
		],
	},
	{
		version: "0.0.9",
		date: "2025-09-17",
		type: "patch",
		summary: "Lokalisasi halaman instances ke bahasa Indonesia",
		changes: [
			{
				type: "changed",
				description:
					"Terjemahan lengkap halaman detail instances ke bahasa Indonesia untuk pengalaman pengguna yang lebih baik",
			},
			{
				type: "improved",
				description:
					"Peningkatan pengalaman pengguna lokal dengan antarmuka berbahasa Indonesia yang konsisten",
			},
		],
	},
	{
		version: "0.0.8",
		date: "2025-09-17",
		type: "patch",
		summary: "Perbaikan pengecekan ketersediaan domain",
		changes: [
			{
				type: "fixed",
				description: "Perbaikan masalah pada pengecekan ketersediaan domain di formulir pesanan",
			},
			{
				type: "improved",
				description: "Peningkatan stabilitas dan akurasi validasi domain",
			},
		],
	},
	{
		version: "0.0.7",
		date: "2025-09-17",
		type: "minor",
		summary: "Implementasi sistem tema gelap/terang dan peningkatan fitur domain",
		changes: [
			{
				type: "added",
				description: "Sistem tema gelap/terang yang dapat diubah melalui tombol toggle di header",
			},
			{
				type: "added",
				description:
					"Dukungan lengkap dark mode dengan palet warna yang halus dan nyaman untuk mata",
			},
			{
				type: "added",
				description: "Fitur pengecekan WHOIS domain untuk validasi ketersediaan domain",
			},
			{
				type: "improved",
				description:
					"Penyesuaian otomatis warna antarmuka berdasarkan tema yang dipilih (gelap/terang)",
			},
			{
				type: "improved",
				description:
					"Optimisasi pengalaman pengguna dengan transisi warna yang mulus saat beralih tema",
			},
			{
				type: "changed",
				description:
					"Reset domain secara otomatis ketika mengubah jenis domain di formulir pesanan",
			},
			{
				type: "fixed",
				description: "Perbaikan peringatan dan masalah pada halaman pembuatan pesanan",
			},
			{
				type: "improved",
				description: "Peningkatan stabilitas formulir pesanan dan pengujian komponen",
			},
		],
	},
	{
		version: "0.0.6",
		date: "2025-09-20",
		type: "minor",
		summary: "Peningkatan sistem manajemen pesanan dan perbaikan antarmuka pengguna",
		changes: [
			{
				type: "fixed",
				description: "Perbaikan bug pada pengambilan data branch dari GitHub",
			},
		],
	},
	{
		version: "0.0.5",
		date: "2025-09-14",
		type: "minor",
		summary: "Integrasi GitHub di formulir pesanan dan peningkatan validasi domain",
		changes: [
			{
				type: "added",
				description:
					"Layanan APLA.AP kini mewajibkan pemilihan repository dan branch (metadata.repository dan metadata.branch) di formulir pesanan",
			},
			{
				type: "added",
				description:
					"Tombol 'Sambungkan GitHub' pada formulir pesanan untuk memudahkan integrasi GitHub",
			},
			{
				type: "fixed",
				description: "Perbaikan berulang pada fungsi pengecekan ketersediaan domain",
			},
			{
				type: "improved",
				description: "Penyempurnaan fungsi pengecekan domain dan alur pembuatan pesanan",
			},
			{
				type: "changed",
				description: "Peningkatan antarmuka faktur publik dan penyempurnaan tampilan header",
			},
			{
				type: "changed",
				description:
					"Pembaruan layanan API serta pembenahan terkait migrasi Vite untuk halaman pesanan",
			},
		],
	},
	{
		version: "0.0.4",
		date: "2025-08-29",
		type: "minor",
		summary: "Peningkatan sistem pembayaran publik dan antarmuka faktur yang responsif",
		changes: [
			{
				type: "added",
				description:
					"Halaman faktur publik yang lengkap dengan tampilan invoice detail dan informasi tagihan",
			},
			{
				type: "added",
				description:
					"Sistem pemilihan metode pembayaran dengan berbagai opsi virtual account dan e-wallet",
			},
			{
				type: "added",
				description:
					"Timer countdown pembayaran dengan deteksi otomatis kedaluwarsa dan pembatasan metode pembayaran",
			},
			{
				type: "added",
				description:
					"Floating payment button dengan status dinamis dan akses langsung ke halaman pembayaran",
			},
			{
				type: "improved",
				description:
					"Antarmuka faktur publik yang responsif dan dioptimalkan untuk perangkat mobile",
			},
			{
				type: "improved",
				description:
					"Pengalaman pengguna pembayaran dengan notifikasi status dan panduan visual yang jelas",
			},
			{
				type: "added",
				description:
					"Integrasi pembayaran dengan payment gateway dan penanganan URL pembayaran otomatis",
			},
			{
				type: "added",
				description:
					"Sistem validasi pembayaran aktif yang mencegah duplikasi pembayaran selama proses berlangsung",
			},
			{
				type: "improved",
				description:
					"Desain responsif untuk semua komponen pembayaran dengan optimisasi touch-friendly",
			},
			{
				type: "added",
				description:
					"Drawer pembayaran dengan preview biaya transaksi dan kalkulasi total pembayaran real-time",
			},
		],
	},
	{
		version: "0.0.3",
		date: "2025-08-24",
		type: "minor",
		summary: "Peningkatan sistem manajemen pesanan dan perbaikan antarmuka pengguna",
		changes: [
			{
				type: "added",
				description:
					"Sistem pembuatan pesanan yang lengkap dengan form pemesanan interaktif dan validasi",
			},
			{
				type: "added",
				description: "Halaman pesanan baru dengan listing, filtering, dan manajemen status pesanan",
			},
			{
				type: "improved",
				description:
					"Peningkatan signifikan pada workflow pembuatan pesanan dengan pengalaman pengguna yang lebih baik",
			},
			{
				type: "improved",
				description: "Optimisasi performa dan stabilitas pada fitur manajemen pesanan",
			},
			{
				type: "added",
				description:
					"Fitur checkpoint untuk proses pembuatan pesanan bertahap dan recovery yang lebih baik",
			},
			{
				type: "improved",
				description: "Penyempurnaan halaman detail domain dengan informasi yang lebih komprehensif",
			},
			{
				type: "improved",
				description: "Peningkatan stabilitas dashboard dan perbaikan masalah rendering",
			},
		],
	},
	{
		version: "0.0.2",
		date: "2025-08-23",
		type: "minor",
		summary: "Peningkatan antarmuka pengguna dan fitur pembayaran baru",
		changes: [
			{
				type: "added",
				description:
					"Halaman detail domain dengan manajemen domain komprehensif dan opsi konfigurasi",
			},
			{
				type: "added",
				description:
					"Fitur pembayaran faktur yang memungkinkan pengguna memproses pembayaran secara langsung",
			},
			{
				type: "added",
				description:
					"Kartu deployment untuk visualisasi status dan informasi deployment yang lebih baik",
			},
			{
				type: "added",
				description:
					"Halaman detail instance yang diperluas dengan informasi layanan komprehensif dan manajemen",
			},
			{
				type: "fixed",
				description: "Masalah rendering dashboard dan peningkatan kinerja",
			},
			{
				type: "fixed",
				description: "Masalah fungsionalitas pemilih tim pada komponen navigasi",
			},
			{
				type: "fixed",
				description: "Masalah pengalihan setelah login untuk pengalaman pengguna yang lebih baik",
			},
		],
	},
	{
		version: "0.0.1",
		date: "2025-08-20",
		type: "major",
		summary: "Rilis awal Cloudiva App dengan fitur dashboard dan manajemen komprehensif",
		changes: [
			{
				type: "fixed",
				description:
					"Masalah pemilih tim di komponen header untuk meningkatkan pengalaman pengguna",
			},
			{
				type: "added",
				description:
					"Halaman manajemen instance dengan listing, viewing, dan kemampuan manajemen yang komprehensif",
			},
			{
				type: "added",
				description:
					"Halaman manajemen domain dengan informasi domain terperinci dan fitur administrasi",
			},
			{
				type: "added",
				description:
					"Manajemen faktur yang ditingkatkan dengan fungsi listing dan viewing yang lebih baik",
			},
			{
				type: "added",
				description: "API layanan pengguna untuk manajemen data domain dan instance",
			},
			{
				type: "changed",
				description:
					"Menu tombol profil yang diperbarui di header untuk navigasi pengguna yang lebih baik",
			},
			{
				type: "changed",
				description:
					"Menu navbar yang diperluas dengan organisasi dan aksesibilitas yang lebih baik",
			},
			{
				type: "added",
				description: "Dashboard komprehensif dengan statistik, analitik, dan metrik utama",
			},
			{
				type: "added",
				description:
					"Halaman informasi aplikasi lengkap dengan detail sistem, dependensi, dan changelog",
			},
			{
				type: "added",
				description:
					"Tampilan detail instance dengan informasi server komprehensif dan alat manajemen",
			},
			{
				type: "added",
				description: "Tampilan detail domain dengan informasi pendaftaran dan penagihan",
			},
			{
				type: "added",
				description: "Tampilan detail faktur dengan rincian item dan pelacakan pembayaran",
			},
			{
				type: "added",
				description: "Sistem manajemen pesanan dengan pembuatan, listing, dan tampilan detail",
			},
			{
				type: "added",
				description: "API layanan admin untuk manajemen sistem yang komprehensif",
			},
			{
				type: "improved",
				description: "Styling navbar dan konsistensi antarmuka pengguna",
			},
			{
				type: "changed",
				description:
					"File konfigurasi yang diperbarui untuk setup deployment dan development yang lebih baik",
			},
			{
				type: "changed",
				description:
					"Migrasi dari Create React App ke Vite untuk peningkatan kinerja build dan pengalaman development",
			},
			{
				type: "added",
				description: "Dukungan TypeScript dengan definisi tipe yang komprehensif",
			},
			{
				type: "added",
				description: "Dokumentasi migrasi untuk referensi tim pengembang",
			},
			{
				type: "improved",
				description: "Pembaruan antarmuka minor untuk konsistensi visual yang lebih baik",
			},
			{
				type: "added",
				description:
					"Dashboard dan antarmuka navigasi yang diperluas dengan pengalaman pengguna yang lebih baik",
			},
			{
				type: "added",
				description: "Setup proyek awal berdasarkan sistem konsol Chativa dan Smartiva yang ada",
			},
			{
				type: "added",
				description: "Sistem otentikasi pengguna dan manajemen sesi",
			},
			{
				type: "added",
				description: "Desain responsif yang dioptimalkan untuk perangkat desktop dan mobile",
			},
			{
				type: "added",
				description: "Integrasi dengan pustaka komponen UI Ant Design untuk styling yang konsisten",
			},
			{
				type: "added",
				description: "Integrasi Firebase untuk notifikasi push dan messaging",
			},
			{
				type: "added",
				description: "Dukungan PWA (Progressive Web App) dengan manifest dan service worker",
			},
			{
				type: "added",
				description: "Fungsi utilitas komprehensif untuk formatting dan manajemen waktu",
			},
			{
				type: "added",
				description: "Manajemen state berbasis konteks dengan penanganan state seluruh aplikasi",
			},
		],
	},
];

function AppInformationTab() {
	const navigate = useNavigate();
	const [systemInfo, setSystemInfo] = useState({
		userAgent: "",
		platform: "",
		language: "",
		cookieEnabled: false,
		onLine: false,
		memory: null,
		hardwareConcurrency: 0,
		screen: {
			width: 0,
			height: 0,
			colorDepth: 0,
		},
		timezone: "",
		buildTime: "",
	});

	useEffect(() => {
		document.title = "Cloudiva App | Informasi Aplikasi";

		// Kumpulkan informasi sistem
		const info = {
			userAgent: navigator.userAgent,
			platform: navigator.platform,
			language: navigator.language,
			cookieEnabled: navigator.cookieEnabled,
			onLine: navigator.onLine,
			memory: navigator.deviceMemory || null,
			hardwareConcurrency: navigator.hardwareConcurrency,
			screen: {
				width: window.screen.width,
				height: window.screen.height,
				colorDepth: window.screen.colorDepth,
			},
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			// buildTime: process.env.REACT_APP_BUILD_TIME || dayjs().format("YYYY-MM-DD HH:mm:ss"),
			buildTime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
		};

		setSystemInfo(info);
	}, []);

	// Siapkan data dependensi untuk tabel
	const dependenciesData = Object.entries(packageInfo.dependencies).map(
		([name, version], index) => ({
			key: index,
			name,
			version,
			category: getCategoryByPackageName(name),
		})
	);

	// Fungsi helper untuk mengkategorikan paket
	function getCategoryByPackageName(name) {
		if (name.includes("react")) return "React";
		if (name.includes("antd") || name.includes("ant-design")) return "Framework UI";
		if (name.includes("testing") || name.includes("jest")) return "Pengujian";
		if (name.includes("types") || name.includes("typescript")) return "TypeScript";
		if (name === "axios") return "Klien HTTP";
		if (name === "dayjs") return "Tanggal/Waktu";
		if (name === "firebase") return "Firebase";
		if (name.includes("router")) return "Routing";
		if (name.includes("icon")) return "Ikon";
		if (name.includes("country") || name.includes("phone")) return "Internasionalisasi";
		if (name.includes("color") || name.includes("image")) return "Media/Grafis";
		return "Lainnya";
	}

	// Fungsi helper untuk mendapatkan styling tipe perubahan
	function getChangeTypeConfig(type) {
		const configs = {
			added: { color: "green", icon: "✨", label: "Ditambahkan" },
			changed: { color: "blue", icon: "🔄", label: "Diubah" },
			fixed: { color: "orange", icon: "🐛", label: "Diperbaiki" },
			removed: { color: "red", icon: "❌", label: "Dihapus" },
			security: { color: "purple", icon: "🔒", label: "Keamanan" },
			deprecated: { color: "volcano", icon: "⚠️", label: "Tidak Digunakan" },
			improved: { color: "cyan", icon: "⬆️", label: "Ditingkatkan" },
		};
		return configs[type] || configs.changed;
	}

	// Fungsi helper untuk mendapatkan warna badge versi
	function getVersionBadgeColor(type) {
		const colors = {
			major: "red",
			minor: "blue",
			patch: "green",
		};
		return colors[type] || "default";
	}

	const dependencyColumns = [
		{
			title: "Nama Paket",
			dataIndex: "name",
			key: "name",
			sorter: (a, b) => a.name.localeCompare(b.name),
			render: (text) => <code style={{ fontSize: "12px" }}>{text}</code>,
		},
		{
			title: "Versi",
			dataIndex: "version",
			key: "version",
			sorter: (a, b) => a.version.localeCompare(b.version),
			render: (text) => <Tag color="blue">{text}</Tag>,
		},
		{
			title: "Kategori",
			dataIndex: "category",
			key: "category",
			filters: [
				{ text: "React", value: "React" },
				{ text: "Framework UI", value: "Framework UI" },
				{ text: "Pengujian", value: "Pengujian" },
				{ text: "TypeScript", value: "TypeScript" },
				{ text: "Klien HTTP", value: "Klien HTTP" },
				{ text: "Tanggal/Waktu", value: "Tanggal/Waktu" },
				{ text: "Firebase", value: "Firebase" },
				{ text: "Routing", value: "Routing" },
				{ text: "Ikon", value: "Ikon" },
				{ text: "Internasionalisasi", value: "Internasionalisasi" },
				{ text: "Media/Grafis", value: "Media/Grafis" },
				{ text: "Lainnya", value: "Lainnya" },
			],
			onFilter: (value, record) => record.category === value,
			render: (text) => {
				const colors = {
					React: "volcano",
					"Framework UI": "geekblue",
					Pengujian: "green",
					TypeScript: "blue",
					"Klien HTTP": "purple",
					"Tanggal/Waktu": "orange",
					Firebase: "gold",
					Routing: "lime",
					Ikon: "cyan",
					Internasionalisasi: "magenta",
					"Media/Grafis": "red",
					Lainnya: "default",
				};
				return <Tag color={colors[text] || "default"}>{text}</Tag>;
			},
		},
	];

	const copyToClipboard = () => {
		const appInfo = {
			name: packageInfo.name,
			version: packageInfo.version,
			buildTime: systemInfo.buildTime,
			userAgent: systemInfo.userAgent,
			platform: systemInfo.platform,
			dependencies: packageInfo.dependencies,
		};

		navigator.clipboard
			.writeText(JSON.stringify(appInfo, null, 2))
			.then(() => {
				message.success("Informasi aplikasi berhasil disalin ke clipboard!");
			})
			.catch(() => {
				message.error("Gagal menyalin ke clipboard");
			});
	};

	return (
		<div>
			<div
				style={{
					marginBottom: 16,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}>
				<Title level={5} style={{ margin: 0 }}>
					Informasi Aplikasi
				</Title>
			</div>

			<Row gutter={[24, 24]}>
				{/* App Overview */}
				<Col xs={24} lg={12}>
					<Card
						title={
							<Space>
								<InfoCircleOutlined />
								Ringkasan Aplikasi
							</Space>
						}
						bordered={true}>
						<Descriptions column={1} size="small">
							<Descriptions.Item label="Nama Aplikasi">
								<Text strong>{packageInfo.name}</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Versi">
								<Badge count={packageInfo.version} color="blue" />
							</Descriptions.Item>
							<Descriptions.Item label="Beranda">
								<a href={packageInfo.homepage} target="_blank" rel="noopener noreferrer">
									{packageInfo.homepage}
								</a>
							</Descriptions.Item>
							<Descriptions.Item label="Waktu Build">
								<Text>
									<CalendarOutlined style={{ marginRight: 4 }} />
									{systemInfo.buildTime}
								</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Environment">
								<Tag color={process.env.NODE_ENV === "production" ? "green" : "orange"}>
									{process.env.NODE_ENV || "development"}
								</Tag>
							</Descriptions.Item>
						</Descriptions>

						<Divider />

						<Space>
							<Button type="primary" icon={<CodeOutlined />} onClick={copyToClipboard}>
								Salin Info Aplikasi
							</Button>
							<Button
								icon={<BugOutlined />}
								onClick={() =>
									window.open("https://github.com/codeiva/cloudiva-app/issues", "_blank")
								}>
								Laporkan Masalah
							</Button>
						</Space>
					</Card>
				</Col>

				{/* System Information */}
				<Col xs={24} lg={12}>
					<Card
						title={
							<Space>
								<CloudServerOutlined />
								Informasi Sistem
							</Space>
						}
						bordered={true}>
						<Descriptions column={1} size="small">
							<Descriptions.Item label="Platform">
								<Text code>{systemInfo.platform}</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Bahasa">
								<Text>{systemInfo.language}</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Zona Waktu">
								<Text>
									<GlobalOutlined style={{ marginRight: 4 }} />
									{systemInfo.timezone}
								</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Resolusi Layar">
								<Text>
									{systemInfo.screen.width} × {systemInfo.screen.height}
								</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Kedalaman Warna">
								<Text>{systemInfo.screen.colorDepth} bit</Text>
							</Descriptions.Item>
							<Descriptions.Item label="Core CPU">
								<Badge count={systemInfo.hardwareConcurrency} color="green" />
							</Descriptions.Item>
							{systemInfo.memory && (
								<Descriptions.Item label="Memori Perangkat">
									<Text>{systemInfo.memory} GB</Text>
								</Descriptions.Item>
							)}
							<Descriptions.Item label="Cookie Diaktifkan">
								<Badge
									status={systemInfo.cookieEnabled ? "success" : "error"}
									text={systemInfo.cookieEnabled ? "Ya" : "Tidak"}
								/>
							</Descriptions.Item>
							<Descriptions.Item label="Status Online">
								<Badge
									status={systemInfo.onLine ? "success" : "error"}
									text={systemInfo.onLine ? "Online" : "Offline"}
								/>
							</Descriptions.Item>
						</Descriptions>
					</Card>
				</Col>

				{/* Technology Stack */}
				<Col xs={24}>
					<Card
						title={
							<Space>
								<TeamOutlined />
								Ringkasan Stack Teknologi
							</Space>
						}
						bordered={true}>
						<Row gutter={[16, 16]}>
							<Col xs={24} sm={12} md={8} lg={6}>
								<Card size="small" bordered>
									<div style={{ textAlign: "center" }}>
										<Text strong>Framework Frontend</Text>
										<br />
										<Badge count="React 18.3.1" color="blue" />
									</div>
								</Card>
							</Col>
							<Col xs={24} sm={12} md={8} lg={6}>
								<Card size="small" bordered>
									<div style={{ textAlign: "center" }}>
										<Text strong>Pustaka UI</Text>
										<br />
										<Badge count="Ant Design 5.23.3" color="geekblue" />
									</div>
								</Card>
							</Col>
							<Col xs={24} sm={12} md={8} lg={6}>
								<Card size="small" bordered>
									<div style={{ textAlign: "center" }}>
										<Text strong>Klien HTTP</Text>
										<br />
										<Badge count="Axios 0.26.1" color="purple" />
									</div>
								</Card>
							</Col>
							<Col xs={24} sm={12} md={8} lg={6}>
								<Card size="small" bordered>
									<div style={{ textAlign: "center" }}>
										<Text strong>Alat Build</Text>
										<br />
										<Badge count="Create React App" color="green" />
									</div>
								</Card>
							</Col>
						</Row>
					</Card>
				</Col>

				{/* Release Changelog */}
				<Col xs={24}>
					<Card
						title={
							<Space>
								<HistoryOutlined />
								Riwayat Rilis & Changelog
							</Space>
						}
						bordered={true}>
						<Alert
							message="Informasi Rilis"
							description={
								<div>
									<Paragraph style={{ marginBottom: 8 }}>
										Bagian ini menampilkan riwayat rilis dan perubahan untuk setiap versi aplikasi
										Cloudiva App. Semua rilis dilacak dari commit Git dengan awalan 'release:' untuk
										versioning yang konsisten.
									</Paragraph>
									<Space>
										<Text strong>Total Rilis:</Text>
										<Badge count={changelogData.length} color="blue" />
										<Text strong>Versi Terbaru:</Text>
										<Tag color="green">v{packageInfo.version}</Tag>
									</Space>
								</div>
							}
							type="info"
							showIcon
							style={{ marginBottom: 16 }}
						/>

						<div style={{ maxHeight: "600px", overflowY: "auto" }}>
							{changelogData.map((release, index) => (
								<Card
									key={release.version}
									size="small"
									style={{
										marginBottom: 16,
										border:
											release.version === packageInfo.version
												? "2px solid #1890ff"
												: "1px solid #d9d9d9",
										boxShadow:
											release.version === packageInfo.version
												? "0 2px 8px rgba(24, 144, 255, 0.15)"
												: "none",
									}}
									title={
										<div>
											<Space>
												<Badge
													count={`v${release.version}`}
													color={getVersionBadgeColor(release.type)}
												/>
												{release.version === packageInfo.version && (
													<Tag color="blue">Versi Saat Ini</Tag>
												)}
												<Text type="secondary" style={{ fontSize: "12px" }}>
													<CalendarOutlined style={{ marginRight: 4 }} />
													{release.date}
												</Text>
												<Divider type="vertical" />
												{release.summary && (
													<div>
														<Text
															type="secondary"
															style={{ fontSize: "13px", fontStyle: "italic" }}>
															{release.summary}
														</Text>
													</div>
												)}
											</Space>
										</div>
									}>
									<div style={{ paddingLeft: 8 }}>
										{release.changes.map((change, changeIndex) => {
											const config = getChangeTypeConfig(change.type);
											return (
												<div key={changeIndex} style={{ marginBottom: 8 }}>
													<Space align="start">
														<span style={{ fontSize: "16px", marginTop: "2px" }}>
															{config.icon}
														</span>
														<div>
															<Tag color={config.color} size="small" style={{ marginBottom: 4 }}>
																{config.label}
															</Tag>
															<Text style={{ display: "block", lineHeight: "1.4" }}>
																{change.description}
															</Text>
														</div>
													</Space>
												</div>
											);
										})}
									</div>
								</Card>
							))}
						</div>

						<Divider />

						<div style={{ textAlign: "center" }}>
							<Space direction="vertical" size="small">
								<Text type="secondary" style={{ fontSize: "12px" }}>
									<InfoCircleOutlined style={{ marginRight: 4 }} />
									Menampilkan {changelogData.length} rilis • Perubahan dilacak dari commit Git
									dengan awalan "release:"
								</Text>
								<Text type="secondary" style={{ fontSize: "11px" }}>
									Terbaru: v{changelogData[0]?.version} • Total perubahan:{" "}
									{changelogData.reduce((total, release) => total + release.changes.length, 0)}
								</Text>
							</Space>
						</div>
					</Card>
				</Col>

				{/* Dependencies */}
				<Col xs={24}>
					<Card
						title={
							<Space>
								<GithubOutlined />
								Dependensi ({Object.keys(packageInfo.dependencies).length} paket)
							</Space>
						}
						bordered={true}>
						<Alert
							message="Informasi Paket"
							description="Tabel ini menampilkan semua paket dan pustaka yang digunakan dalam aplikasi ini. Anda dapat memfilter berdasarkan kategori atau mencari paket tertentu."
							type="info"
							showIcon
							style={{ marginBottom: 16 }}
						/>
						<Table
							columns={dependencyColumns}
							dataSource={dependenciesData}
							size="small"
							pagination={{
								pageSize: 10,
								showSizeChanger: true,
								showQuickJumper: true,
								showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} paket`,
							}}
							scroll={{ x: true }}
							bordered
						/>
					</Card>
				</Col>
			</Row>
		</div>
	);
}

export default AppInformationTab;
